import { useRef } from "react";
import { Box, Heading, Text, HStack, Button } from "@chakra-ui/react";
import { motion } from "framer-motion";
import callAgent from '../assets/call-center.jpg';
const MotionBox = motion(Box);

export default function HomeCTA({ onOpenModal }) {
  const sectionRef = useRef(null);

  return (
    <Box
      ref={sectionRef}
      bg="brand.sage"
      py={{ base: 20, md: 28 }}
      px={{ base: 6, md: 12, lg: 20 }}
      position="relative"
      overflow="hidden"
    >
      {/* ── PARALLAX BACKGROUND IMAGE ── */}
      <Box position="absolute" inset="0" overflow="hidden" zIndex={0}>
        <MotionBox
          position="absolute"
          inset="0"
          backgroundImage={`url(${callAgent})`}
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundAttachment="fixed"
        />
        {/* Dark overlay for readability */}
        <Box
          position="absolute"
          inset="0"
          background="linear-gradient(180deg, rgba(89,92,75,0.88) 0%, rgba(89,92,75,0.80) 50%, rgba(89,92,75,0.92) 100%)"
        />
        {/* Subtle coral tint */}
        <Box
          position="absolute"
          inset="0"
          background="linear-gradient(135deg, rgba(224,92,58,0.08) 0%, transparent 60%)"
        />
      </Box>

      {/* ── GEO SHAPES — above image, below content ── */}

      {/* Large outer spinning ring — top right */}
      <Box
        position="absolute" top="-80px" right="-80px"
        w={{ base: "280px", md: "460px" }} h={{ base: "280px", md: "460px" }}
        borderRadius="50%"
        border="1px solid rgba(245,240,235,0.1)"
        animation="slowSpin 32s linear infinite"
        pointerEvents="none" zIndex={1}
      />
      {/* Mid ring */}
      <Box
        position="absolute" top="-20px" right="-20px"
        w={{ base: "160px", md: "270px" }} h={{ base: "160px", md: "270px" }}
        borderRadius="50%"
        border="1px solid rgba(224,92,58,0.25)"
        pointerEvents="none" zIndex={1}
      />
      {/* Small glow dot */}
      <Box
        position="absolute" top="60px" right="60px"
        w={{ base: "70px", md: "120px" }} h={{ base: "70px", md: "120px" }}
        borderRadius="50%"
        background="radial-gradient(circle, rgba(224,92,58,0.2) 0%, transparent 70%)"
        border="1px solid rgba(224,92,58,0.3)"
        pointerEvents="none" zIndex={1}
      />

      {/* Bottom-left reverse spinning ring */}
      <Box
        position="absolute" bottom="-60px" left="-60px"
        w={{ base: "200px", md: "320px" }} h={{ base: "200px", md: "320px" }}
        borderRadius="50%"
        border="1px solid rgba(245,240,235,0.08)"
        animation="slowSpinReverse 40s linear infinite"
        pointerEvents="none" zIndex={1}
      />

      {/* Corner brackets */}
      <Box
        position="absolute" top="24px" left="24px" w="44px" h="44px"
        borderTop="1.5px solid rgba(224,92,58,0.45)"
        borderLeft="1.5px solid rgba(224,92,58,0.45)"
        pointerEvents="none" zIndex={2}
      />
      <Box
        position="absolute" top="24px" right="24px" w="44px" h="44px"
        borderTop="1.5px solid rgba(224,92,58,0.45)"
        borderRight="1.5px solid rgba(224,92,58,0.45)"
        pointerEvents="none" zIndex={2}
      />
      <Box
        position="absolute" bottom="24px" left="24px" w="44px" h="44px"
        borderBottom="1.5px solid rgba(224,92,58,0.45)"
        borderLeft="1.5px solid rgba(224,92,58,0.45)"
        pointerEvents="none" zIndex={2}
      />
      <Box
        position="absolute" bottom="24px" right="24px" w="44px" h="44px"
        borderBottom="1.5px solid rgba(224,92,58,0.45)"
        borderRight="1.5px solid rgba(224,92,58,0.45)"
        pointerEvents="none" zIndex={2}
      />

      {/* Diagonal accent lines */}
      <Box
        position="absolute" top="35%" left="4%"
        w="70px" h="1px" bg="rgba(224,92,58,0.25)"
        transform="rotate(-40deg)"
        pointerEvents="none" zIndex={2}
      />
      <Box
        position="absolute" bottom="28%" right="4%"
        w="55px" h="1px" bg="rgba(224,92,58,0.25)"
        transform="rotate(-40deg)"
        pointerEvents="none" zIndex={2}
      />

      {/* Grid texture */}
      <Box
        position="absolute"
        inset="0"
        opacity="0.025"
        backgroundImage="linear-gradient(#F5F0EB 1px, transparent 1px), linear-gradient(90deg, #F5F0EB 1px, transparent 1px)"
        backgroundSize="50px 50px"
        pointerEvents="none"
        zIndex={2}
      />

      {/* ── CONTENT ── */}
      <MotionBox
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        maxW="1400px"
        mx="auto"
        textAlign="center"
        position="relative"
        zIndex={3}
      >
        <Heading
          fontFamily="'Playfair Display', serif"
          fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
          fontWeight="400"
          color="brand.cream"
          lineHeight="1.25"
          mb={4}
        >
          Ready to grow your
          <Text as="span" fontStyle="italic" color="brand.coralPale">
            {" "}
            property listings?
          </Text>
        </Heading>
        <Text
          color="rgba(245,240,235,0.65)"
          mb={10}
          maxW="480px"
          mx="auto"
          lineHeight="1.9"
          fontSize="md"
        >
          Whether you're buying, selling, or letting us prospect for you —
          we're ready to help you grow.
        </Text>
        <HStack justify="center" spacing={4} wrap="wrap">
          <Button variant="coral" onClick={onOpenModal}>
            I'm an Agent
          </Button>
          <Button variant="outlineLight" onClick={onOpenModal}>
            I'm a Landlord
          </Button>
        </HStack>
      </MotionBox>

      <style>{`
        @keyframes slowSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slowSpinReverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
      `}</style>
    </Box>
  );
}